import { z } from "zod";

import type { OperatorChartDataSliceOptions } from "./types";

export const maxInlineOperatorChartDescriptionBytes = 5_000;
export const maxOperatorChartToolPoints = 1_000;

export const chartDataToolDescription =
  "Read a bounded slice of the time-series data for a chart visible on the current page. Use seriesNames and point indexes to narrow large charts, and use the chart id from read_current_page as toolEntityId.";

export const chartDataParameters: z.ZodType<OperatorChartDataSliceOptions> = z.object({
  seriesNames: z.array(z.string()).nullable().describe("Chart series names to return, or null for all series."),
  startIndex: z.number().int().min(0).nullable().describe("Zero-based point index, or null to start at zero."),
  maxPoints: z
    .number()
    .int()
    .min(1)
    .max(maxOperatorChartToolPoints)
    .nullable()
    .describe("Maximum total points to return across the selected series, or null to use the 1,000 point limit."),
});

const getFrozenPointKeys = (...keys: string[]): readonly string[] => Object.freeze(keys);

export const timeSeriesPointKeys: readonly string[] = Object.freeze([
  "x",
  "x2",
  "y",
  "name",
  "low",
  "q1",
  "median",
  "q3",
  "high",
  "open",
  "close",
  "z",
  "value",
  "length",
  "direction",
]);

export const defaultPointKeysBySeriesType: Readonly<Record<string, readonly string[]>> = Object.freeze({
  arearange: getFrozenPointKeys("x", "low", "high"),
  areasplinerange: getFrozenPointKeys("x", "low", "high"),
  boxplot: getFrozenPointKeys("x", "low", "q1", "median", "q3", "high"),
  bubble: getFrozenPointKeys("x", "y", "z"),
  candlestick: getFrozenPointKeys("x", "open", "high", "low", "close"),
  columnrange: getFrozenPointKeys("x", "low", "high"),
  ohlc: getFrozenPointKeys("x", "open", "high", "low", "close"),
  vector: getFrozenPointKeys("x", "y", "length", "direction"),
  windbarb: getFrozenPointKeys("x", "value", "direction"),
  xrange: getFrozenPointKeys("x", "x2", "y"),
});
